import { Field, Form, FormikProps } from 'formik';

import { ContainerInstanceFormValues } from '@/react/azure/types';
import { useVirtualNetworks } from '@/react/azure/queries/useVirtualNetworks';
import { useLocationOptionsWithVNs } from '@/react/azure/container-instances/CreateView/utils';
import { TagsField } from '@/react/azure/container-instances/CreateView/TagsField';
import { NetworksFormSection } from '@/react/azure/container-instances/CreateView/NetworksFormSection';
import { VolumeFormSection } from '@/react/azure/container-instances/CreateView/VolumeFormSection';
import { GPUFormSection } from '@/react/azure/container-instances/CreateView/GPUFormSection';
import { AccessControlForm } from '@/react/portainer/access-control';

import { EnvironmentVariablesPanel } from '@@/form-components/EnvironmentVariablesFieldset';
import { Input } from '@@/form-components/Input';
import { FormControl } from '@@/form-components/FormControl';
import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { Option, PortainerSelect } from '@@/form-components/PortainerSelect';
import { FormActions } from '@@/form-components/FormActions';

type Props = FormikProps<ContainerInstanceFormValues> & {
  resourceGroupOptions: Option<string>[];
  subscriptionOptions: Option<string>[];
  environmentId: number;
  locationOptions: Option<string>[];
  isSubscriptionsLoading: boolean;
  isResourceGroupsLoading: boolean;
  isProvidersLoading: boolean;
};

export function CreateContainerInstanceInnerForm({
  errors,
  handleSubmit,
  isSubmitting,
  isValid,
  values,
  setFieldValue,
  environmentId,
  resourceGroupOptions,
  subscriptionOptions,
  locationOptions,
  isSubscriptionsLoading,
  isResourceGroupsLoading,
  isProvidersLoading,
}: Props) {
  // repeat the useVirtualNetworks query, because the selected subscription may change
  const virtualNetworksQuery = useVirtualNetworks(
    environmentId,
    values.subscription || ''
  );
  const virtualNetworks = virtualNetworksQuery.data || [];
  const updatedLocationOptions = useLocationOptionsWithVNs(
    locationOptions,
    virtualNetworks
  );

  return (
    <Form className="form-horizontal" onSubmit={handleSubmit} noValidate>
      <FormSectionTitle>Azure Settings</FormSectionTitle>
      <FormControl
        label="Subscription"
        inputId="subscription-input"
        isLoading={isSubscriptionsLoading}
        loadingText="Loading subscriptions..."
        errors={errors.subscription}
      >
        <Field
          name="subscription"
          as={PortainerSelect}
          onChange={(selectedOption: string) =>
            setFieldValue('subscription', selectedOption)
          }
          id="subscription-input"
          options={subscriptionOptions}
          data-cy="subscription-select"
        />
      </FormControl>
      <FormControl
        label="Resource Group"
        inputId="resourceGroup-input"
        isLoading={isResourceGroupsLoading}
        loadingText="Loading resource groups..."
        errors={errors.resourceGroup}
      >
        <Field
          name="resourceGroup"
          as={PortainerSelect}
          onChange={(selectedOption: string) =>
            setFieldValue('resourceGroup', selectedOption)
          }
          id="resourceGroup-input"
          options={resourceGroupOptions}
          data-cy="resource-group-select"
        />
      </FormControl>
      <FormControl
        label="Location"
        inputId="location-input"
        isLoading={isProvidersLoading || virtualNetworksQuery.isLoading}
        loadingText="Loading locations..."
        errors={errors.location}
      >
        <Field
          name="location"
          as={PortainerSelect}
          onChange={(selectedOption: string) =>
            setFieldValue('location', selectedOption)
          }
          id="location-input"
          options={updatedLocationOptions}
          data-cy="location-select"
        />
      </FormControl>
      <FormSectionTitle>Container Configuration</FormSectionTitle>
      <FormControl
        label="Name"
        inputId="name-input"
        errors={errors.name}
        required
      >
        <Field
          name="name"
          as={Input}
          id="name-input"
          placeholder="e.g. myContainer"
          data-cy="name-input"
        />
      </FormControl>
      <FormControl
        label="Image"
        inputId="image-input"
        errors={errors.image}
        required
      >
        <Field
          name="image"
          as={Input}
          id="image-input"
          placeholder="e.g. nginx:alpine"
          data-cy="image-input"
        />
      </FormControl>
      <FormControl label="OS" inputId="os-input" errors={errors.os}>
        <Field
          name="os"
          as={PortainerSelect}
          id="os-input"
          options={[
            { label: 'Linux', value: 'Linux' },
            { label: 'Windows', value: 'Windows' },
          ]}
          onChange={(selectedOption: string) =>
            setFieldValue('os', selectedOption)
          }
          data-cy="os-select"
        />
      </FormControl>
      <EnvironmentVariablesPanel
        values={values.env}
        onChange={(env) => setFieldValue('env', env)}
        errors={errors.env}
      />
      <TagsField />
      <NetworksFormSection
        environmentId={environmentId}
        locationOptions={locationOptions}
      />
      <FormSectionTitle>Container Resources</FormSectionTitle>
      <FormControl label="CPU" inputId="cpu-input" errors={errors.cpu} required>
        <Field
          name="cpu"
          as={Input}
          min={1}
          id="cpu-input"
          type="number"
          placeholder="1"
          data-cy="cpu-input"
        />
      </FormControl>
      <FormControl
        label="Memory (GB)"
        inputId="cpu-input"
        errors={errors.memory}
        required
      >
        <Field
          name="memory"
          as={Input}
          min={1}
          id="memory-input"
          type="number"
          placeholder="1"
          data-cy="memory-input"
        />
      </FormControl>
      <VolumeFormSection />
      <GPUFormSection />
      <AccessControlForm
        formNamespace="accessControl"
        onChange={(values) => setFieldValue('accessControl', values)}
        values={values.accessControl}
        errors={errors.accessControl}
        environmentId={environmentId}
      />
      <FormActions
        submitLabel="Deploy the container"
        loadingText="Deployment in progress..."
        isLoading={isSubmitting}
        isValid={isValid}
        data-cy="aci-create-actions"
      />
    </Form>
  );
}
